import { useId, type ReactNode } from "react";
import { MASK_CUT, MASK_KEEP } from "@/components/common/svg-mask";

export type IconName =
	| "plus"
	| "search"
	| "grid"
	| "video"
	| "file"
	| "filePlus"
	| "folderPlus"
	| "chevronR"
	| "chevronD"
	| "chevronU"
	| "chevronsL"
	| "chevronsR"
	| "arrowL"
	| "arrowR"
	| "arrowD"
	| "arrowU"
	| "arrowUpRight"
	| "clock"
	| "agent"
	| "rocket"
	| "user"
	| "users"
	| "sparkle"
	| "check"
	| "checkCircle"
	| "x"
	| "settings"
	| "bell"
	| "paperclip"
	| "copy"
	| "pencil"
	| "rotate"
	| "restart"
	| "triangleUp"
	| "triangleDown"
	| "stop"
	| "play"
	| "trash"
	| "menu"
	| "kebab"
	| "chat"
	| "chatPlus"
	| "earth"
	| "chip"
	| "store"
	| "terminal"
	| "info"
	| "helpCircle"
	| "helpBubble"
	| "alertCircle"
	| "alertTriangle"
	| "xCircle"
	| "splitRight"
	| "unsplit"
	| "panelLeft"
	| "pin"
	| "creditCard"
	| "database"
	| "databaseUpload"
	| "monitor"
	| "key"
	| "archive"
	| "filter"
	| "branch"
	| "undo";

interface IconProps {
	name: IconName;
	size?: number;
	color?: string;
	stroke?: number;
}

const FILE_OUTLINE_PATH = "M9 1.5H3.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V6L9 1.5z";
// 歯を丸い山でつないだ6歯歯車。角を立てないので 16px でも線が混み合わない。
const GEAR_OUTLINE_PATH =
	"M6.45 2.76a1.56 1.56 0 0 1 3.11 0 1.56 1.56 0 0 0 2.21 1.28 1.56 1.56 0 0 1 1.55 2.69" +
	" 1.56 1.56 0 0 0 0 2.55 1.56 1.56 0 0 1-1.55 2.69 1.56 1.56 0 0 0-2.21 1.28" +
	" 1.56 1.56 0 0 1-3.11 0 1.56 1.56 0 0 0-2.21-1.28 1.56 1.56 0 0 1-1.55-2.69" +
	" 1.56 1.56 0 0 0 0-2.55 1.56 1.56 0 0 1 1.55-2.69 1.56 1.56 0 0 0 2.21-1.28z";
const CHAT_OUTLINE_PATH = "M4 2.5H12a2 2 0 0 1 2 2V8a2 2 0 0 1-2 2H5.5L2 13.5V4.5a2 2 0 0 1 2-2z";
const FOLDER_OUTLINE_PATH =
	"M13.33 13.83a1.33 1.33 0 0 0 1.33-1.33V5.83a1.33 1.33 0 0 0-1.33-1.33H8.06a1.33 1.33 0 0 1-1.13-.6L6.4 3.1" +
	"A1.33 1.33 0 0 0 5.29 2.5H2.67a1.33 1.33 0 0 0-1.33 1.33v8.67a1.33 1.33 0 0 0 1.33 1.33z";

const BADGE_CX = 12;
const BADGE_CY = 12;
const BADGE_R = 4.4;
// 塗りの円と下地の線の間に空ける隙間。
const BADGE_GAP = 0.6;
// 「+」の腕の長さは円の半径に連動させ、半径を調整してもパスを手計算し直さずに済むようにする。
const BADGE_PLUS_ARM = BADGE_R / 2;
const BADGE_PLUS_PATH =
	`M${BADGE_CX} ${BADGE_CY - BADGE_PLUS_ARM}v${BADGE_PLUS_ARM * 2}` +
	`M${BADGE_CX - BADGE_PLUS_ARM} ${BADGE_CY}h${BADGE_PLUS_ARM * 2}`;

/**
 * file/folder/chat の輪郭をバッジ円の位置で切り抜いて描画し、右下角に「+」を抜いた塗りの円を重ねる。
 * マスクで実際に線と「+」を抜くため、ボタンの背景色に関わらず下地が透けない。
 * バッジは viewBox (0 0 16 16) の右下角からはみ出すため、描画側で `overflow: visible` を指定する必要がある。
 */
function BadgedOutline({
	maskId,
	color,
	children,
}: {
	maskId: string;
	color: string;
	children: ReactNode;
}) {
	const outlineMaskId = `${maskId}-outline`;
	const plusMaskId = `${maskId}-plus`;
	return (
		<>
			<defs>
				<mask id={outlineMaskId} maskUnits="userSpaceOnUse" x="-6" y="-6" width="28" height="28">
					<rect x="-6" y="-6" width="28" height="28" fill={MASK_KEEP} />
					<circle
						cx={BADGE_CX}
						cy={BADGE_CY}
						r={BADGE_R + BADGE_GAP}
						fill={MASK_CUT}
						stroke="none"
					/>
				</mask>
				<mask id={plusMaskId} maskUnits="userSpaceOnUse" x="-6" y="-6" width="28" height="28">
					<rect x="-6" y="-6" width="28" height="28" fill={MASK_KEEP} />
					<path d={BADGE_PLUS_PATH} stroke={MASK_CUT} />
				</mask>
			</defs>
			<g mask={`url(#${outlineMaskId})`}>{children}</g>
			<circle
				cx={BADGE_CX}
				cy={BADGE_CY}
				r={BADGE_R}
				fill={color}
				stroke="none"
				mask={`url(#${plusMaskId})`}
			/>
		</>
	);
}

export function Icon({ name, size = 16, color = "currentColor", stroke = 1.3 }: IconProps) {
	const badgeMaskId = useId();
	const common = {
		width: size,
		height: size,
		viewBox: "0 0 16 16",
		fill: "none",
		stroke: color,
		strokeWidth: stroke,
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
		"data-icon": name,
		style: { flexShrink: 0 },
	};

	switch (name) {
		case "plus":
			return (
				<svg {...common}>
					<path d="M8 3v10M3 8h10" />
				</svg>
			);
		case "search":
			return (
				<svg {...common}>
					<circle cx="7" cy="7" r="5.5" />
					<path d="M11 11 14 14" />
				</svg>
			);
		case "grid":
			return (
				<svg {...common}>
					<rect x="2" y="2" width="5" height="5" />
					<rect x="9" y="2" width="5" height="5" />
					<rect x="2" y="9" width="5" height="5" />
					<rect x="9" y="9" width="5" height="5" />
				</svg>
			);
		case "video":
			return (
				<svg {...common}>
					<rect x="1.5" y="3.5" width="9.5" height="9" rx="1" />
					<path d="M11 7l3.5-2v6L11 9z" />
				</svg>
			);
		case "file":
			return (
				<svg {...common}>
					<path d={FILE_OUTLINE_PATH} />
					<path d="M9 1.5V6h4.5" />
				</svg>
			);
		case "filePlus":
			return (
				<svg {...common} style={{ ...common.style, overflow: "visible" }}>
					<BadgedOutline maskId={badgeMaskId} color={color}>
						<path d={FILE_OUTLINE_PATH} />
						<path d="M9 1.5V6h4.5" />
					</BadgedOutline>
				</svg>
			);
		case "folderPlus":
			return (
				<svg {...common} style={{ ...common.style, overflow: "visible" }}>
					<BadgedOutline maskId={badgeMaskId} color={color}>
						<path d={FOLDER_OUTLINE_PATH} />
					</BadgedOutline>
				</svg>
			);
		case "chevronR":
			return (
				<svg {...common}>
					<path d="M5.75 3.5l4.5 4.5-4.5 4.5" />
				</svg>
			);
		case "chevronD":
			return (
				<svg {...common}>
					<path d="M3.5 5.75l4.5 4.5 4.5-4.5" />
				</svg>
			);
		case "chevronU":
			return (
				<svg {...common}>
					<path d="M3.5 10.25l4.5-4.5 4.5 4.5" />
				</svg>
			);
		case "arrowL":
			return (
				<svg {...common}>
					<path d="M13 8H3M7 4l-4 4 4 4" />
				</svg>
			);
		case "arrowR":
			return (
				<svg {...common}>
					<path d="M3 8h10M9 4l4 4-4 4" />
				</svg>
			);
		case "arrowD":
			return (
				<svg {...common}>
					<path d="M8 3v10M4 9l4 4 4-4" />
				</svg>
			);
		case "arrowU":
			return (
				<svg {...common}>
					<path d="M8 13V3M4 7l4-4 4 4" />
				</svg>
			);
		case "arrowUpRight":
			return (
				<svg {...common}>
					<path d="M5 11L11 5M6 5h5v5" />
				</svg>
			);
		case "clock":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="5.5" />
					<path d="M8 5v3l2 1.5" />
				</svg>
			);
		case "agent":
			return (
				<svg {...common}>
					<rect x="2.5" y="5" width="11" height="8.5" rx="2.2" />
					<path d="M8 5V3.2M6 8.5v1.5M10 8.5v1.5" />
					<circle cx="8" cy="2.2" r="1" />
				</svg>
			);
		case "rocket":
			// 右上へ打ち上がるロケット。機体・窓・左右のフィン・噴射炎を描き、
			// 中心を軸に右へ 45 度傾けて上昇感を出す。
			return (
				<svg {...common}>
					<g transform="rotate(45 8 8)">
						<path d="M8 1.5C6 3.5 5.5 6 5.5 9v1.5h5V9c0-3-.5-5.5-2.5-7.5z" />
						<circle cx="8" cy="5.5" r="1.1" />
						<path d="M5.5 8.5 3.5 11l2 -.5M10.5 8.5 12.5 11l-2 -.5" />
						<path d="M6.6 11.2 8 14.5l1.4 -3.3" />
					</g>
				</svg>
			);
		case "user":
			return (
				<svg {...common}>
					<circle cx="8" cy="6" r="2.5" />
					<path d="M3 13.5c.6-2.4 2.6-3.8 5-3.8s4.4 1.4 5 3.8" />
				</svg>
			);
		case "users":
			return (
				<svg {...common}>
					<circle cx="8" cy="5.8" r="2.1" />
					<path d="M4.6 13.5c.5-2.1 1.8-3.3 3.4-3.3s2.9 1.2 3.4 3.3" />
					<circle cx="3.6" cy="6.9" r="1.6" />
					<path d="M1 12.6c.3-1.5 1.1-2.4 2.5-2.5" />
					<circle cx="12.4" cy="6.9" r="1.6" />
					<path d="M15 12.6c-.3-1.5-1.1-2.4-2.5-2.5" />
				</svg>
			);
		case "sparkle":
			return (
				<svg {...common}>
					<path d="M8 2v4M8 10v4M2 8h4M10 8h4" />
				</svg>
			);
		case "check":
			return (
				<svg {...common}>
					<path d="M3 8.5L6.5 12 13 4.5" />
				</svg>
			);
		case "checkCircle":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M5.5 8.2l1.8 1.8L10.6 6.3" />
				</svg>
			);
		case "x":
			return (
				<svg {...common}>
					<path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
				</svg>
			);
		case "settings":
			return (
				<svg {...common}>
					<path d={GEAR_OUTLINE_PATH} />
					<circle cx="8" cy="8" r="2" />
				</svg>
			);
		case "bell":
			return (
				<svg {...common}>
					<path d="M4 12h8M5 12V7a3 3 0 0 1 6 0v5M7 13.5c.2.5.6.8 1 .8s.8-.3 1-.8" />
				</svg>
			);
		case "paperclip":
			return (
				<svg {...common}>
					<path d="M11.5 7.5L7 12a2.5 2.5 0 0 1-3.5-3.5L8 4a1.5 1.5 0 0 1 2.1 2.1L5.6 10.7" />
				</svg>
			);
		case "copy":
			return (
				<svg {...common}>
					<rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
					<path d="M3 10.5A1.5 1.5 0 0 1 1.5 9V3A1.5 1.5 0 0 1 3 1.5h6A1.5 1.5 0 0 1 10.5 3" />
				</svg>
			);
		case "pencil":
			return (
				<svg {...common}>
					<path d="M14.12 4.54a.67.67 0 0 0-2.66-2.66L2.56 10.78a1.33 1.33 0 0 0-.33.55l-.88 2.9a.33.33 0 0 0 .42.41l2.9-.88a1.33 1.33 0 0 0 .55-.33z" />
				</svg>
			);
		case "rotate":
			// Curved arrow (retry / redo) — 3/4 circle with an arrowhead at the top-right.
			return (
				<svg {...common}>
					<path d="M13 8a5 5 0 1 1-1.5-3.5" />
					<path d="M13 3v2.5H10.5" />
				</svg>
			);
		case "restart":
			// 丸矢印の中に再生の三角。停止してから起動し直す操作を、再読み込みの rotate と区別して表す。
			return (
				<svg {...common}>
					<path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
					<path d="M12.5 1.8v2.7H9.8" />
					<path d="M6.9 6v4l3.2-2z" fill={color} />
				</svg>
			);
		case "undo":
			// 左向きの矢印から右へ回り込む曲線。取り消し (元に戻す) 操作を表し、ゴミ箱と対にして使う。
			return (
				<svg {...common}>
					<path d="M3 6.5h6.5a3.25 3.25 0 0 1 0 6.5H7" />
					<path d="M5.5 4 3 6.5 5.5 9" />
				</svg>
			);
		case "triangleUp":
			// Filled upward triangle (thumbs-up surrogate). Uses fill=currentColor
			// so the button's text color cascades into the icon.
			return (
				<svg {...common} fill={color} stroke="none">
					<path d="M8 4 L13 12 H3 Z" />
				</svg>
			);
		case "triangleDown":
			return (
				<svg {...common} fill={color} stroke="none">
					<path d="M8 12 L13 4 H3 Z" />
				</svg>
			);
		case "stop":
			return (
				<svg {...common} fill={color} stroke="none">
					<rect x="4" y="4" width="8" height="8" rx="1.5" />
				</svg>
			);
		case "play":
			return (
				<svg {...common} fill={color} stroke="none">
					<path d="M5 3.9v8.2a.9.9 0 0 0 1.37.77l6.5-4.1a.9.9 0 0 0 0-1.54l-6.5-4.1A.9.9 0 0 0 5 3.9z" />
				</svg>
			);
		case "trash":
			return (
				<svg {...common}>
					<path d="M2.5 4h11" />
					<path d="M12.5 4v9a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 13V4" />
					<path d="M5.5 4V2.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V4" />
					<path d="M6.5 7v4.5M9.5 7v4.5" />
				</svg>
			);
		case "menu":
			// Half-pixel y offsets so a 1px stroke lands fully inside a pixel row
			// at 1x density (size === 16). Avoids blurry horizontal lines.
			return (
				<svg {...common}>
					<path d="M2.5 4.5h11M2.5 8.5h11M2.5 12.5h11" />
				</svg>
			);
		case "kebab":
			return (
				<svg {...common} fill={color} stroke="none" data-icon="kebab">
					<circle cx="8" cy="3.5" r="1.25" />
					<circle cx="8" cy="8" r="1.25" />
					<circle cx="8" cy="12.5" r="1.25" />
				</svg>
			);
		case "chevronsL":
			return (
				<svg {...common}>
					<path d="M8.25 3.5l-4.5 4.5 4.5 4.5M13.25 3.5l-4.5 4.5 4.5 4.5" />
				</svg>
			);
		case "chevronsR":
			return (
				<svg {...common}>
					<path d="M2.75 3.5l4.5 4.5-4.5 4.5M7.75 3.5l4.5 4.5-4.5 4.5" />
				</svg>
			);
		case "chat":
			return (
				<svg {...common}>
					<path d={CHAT_OUTLINE_PATH} />
				</svg>
			);
		case "chatPlus":
			return (
				<svg {...common} style={{ ...common.style, overflow: "visible" }}>
					<BadgedOutline maskId={badgeMaskId} color={color}>
						<path d={CHAT_OUTLINE_PATH} />
					</BadgedOutline>
				</svg>
			);
		case "branch":
			// 縦の幹から右上の節へ枝が分かれるアイコン。会話をこの地点から分岐する操作を表す。
			return (
				<svg {...common}>
					<circle cx="4.5" cy="3.5" r="1.5" />
					<circle cx="4.5" cy="12.5" r="1.5" />
					<circle cx="11.5" cy="5" r="1.5" />
					<path d="M4.5 5v6" />
					<path d="M11.5 6.5c0 2.2-1.6 3.4-3.5 3.6-1.1.1-2.1.5-2.7 1.1" />
				</svg>
			);
		case "filter":
			// 漏斗。絞り込みボタンに使う。
			return (
				<svg {...common}>
					<path d="M1.5 2.5h13L9.5 8.5v5l-3 1.5v-6.5L1.5 2.5z" />
				</svg>
			);
		case "archive":
			// 蓋付きの保管箱。アーカイブ済みチャットの絞り込みに使う。
			return (
				<svg {...common}>
					<path d="M2 3.5h12v3H2z" />
					<path d="M3 6.5v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-6" />
					<path d="M6.5 9.5h3" />
				</svg>
			);
		case "chip":
			return (
				<svg {...common}>
					<rect x="4" y="4" width="8" height="8" rx="1.5" />
					<rect x="6.5" y="6.5" width="3" height="3" rx=".5" />
					<path d="M6.5 2v2M9.5 2v2M6.5 12v2M9.5 12v2M2 6.5h2M2 9.5h2M12 6.5h2M12 9.5h2" />
				</svg>
			);
		case "terminal":
			return (
				<svg {...common}>
					<rect x="2" y="3" width="12" height="10" rx="1.5" />
					<path d="M5 6.5 7 8l-2 1.5M8.5 10h2.5" />
				</svg>
			);
		case "store":
			// 日よけ付きの店先。日よけの裾を3つの波で描き、下に扉を置く。
			return (
				<svg {...common}>
					<path d="M3.3 2.5h9.4L14 6H2l1.3-3.5z" />
					<path d="M2 6c0 .9.9 1.6 2 1.6S6 6.9 6 6c0 .9.9 1.6 2 1.6s2-.7 2-1.6c0 .9.9 1.6 2 1.6s2-.7 2-1.6" />
					<path d="M3 8v5.5h10V8M6.5 13.5V10.5h3v3" />
				</svg>
			);
		case "earth":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M2 8h12" />
					<ellipse cx="8" cy="8" rx="3" ry="6" />
				</svg>
			);
		case "info":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M8 7.5v3.5" />
					<path d="M8 5.25h.01" />
				</svg>
			);
		case "helpCircle":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M6.5 6.25a1.65 1.65 0 0 1 3.2.55c0 1.65-1.7 1.7-1.7 3" />
					<path d="M8 11.75h.01" />
				</svg>
			);
		case "helpBubble":
			return (
				<svg {...common}>
					<path d="M5.38 13.59A6.9 6.5 0 1 0 2.32 10.95L1.4 14.4z" />
					<path d="M6.91 5.68a1.49 1.49 0 0 1 2.88.5c0 1.49-1.53 1.53-1.53 2.7" />
					<path d="M8.26 10.68h.01" />
				</svg>
			);
		case "alertCircle":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M8 5v3.5" />
					<path d="M8 10.75h.01" />
				</svg>
			);
		case "panelLeft":
			// 正方形の中央より左に縦線を引いたアイコン。サイドバーの開閉を表す。
			return (
				<svg {...common}>
					<rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
					<path d="M6 2.5v11" />
				</svg>
			);
		case "splitRight":
			// 正方形を縦に二等分したアイコン。エディタを右に分割する操作を表す。
			return (
				<svg {...common}>
					<rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
					<path d="M8 2.5v11" />
				</svg>
			);
		case "unsplit":
			// splitRight から分割線を除いた枠のみのアイコン。分割解除 (単一ペインに戻す) を表す。
			return (
				<svg {...common}>
					<rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
				</svg>
			);
		case "monitor":
			return (
				<svg {...common}>
					<rect x="2" y="2.5" width="12" height="8.5" rx="1" />
					<path d="M8 11v2.5M5 13.5h6" />
				</svg>
			);
		case "database":
			// ストレージ (円筒)。上面の楕円 + 側面 + 中段と底の弧。
			return (
				<svg {...common}>
					<ellipse cx="8" cy="4" rx="5.5" ry="2" />
					<path d="M2.5 4v8c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2V4" />
					<path d="M2.5 8c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2" />
				</svg>
			);
		case "databaseUpload":
			// 共有ボリュームへの反映。左上に寄せた小さな円筒 + 右下の上向き矢印。
			return (
				<svg {...common}>
					<ellipse cx="6.5" cy="4" rx="4.5" ry="1.7" />
					<path d="M2 4v7.5c0 .9 2 1.7 4.5 1.7" />
					<path d="M11 4v3" />
					<path d="M2 7.8c0 .9 2 1.7 4.5 1.7" />
					<path d="M12 15V9.5M9.7 11.8 12 9.5l2.3 2.3" />
				</svg>
			);
		case "key":
			// 鍵。頭の輪 + 軸 + 歯 2 本。
			return (
				<svg {...common}>
					<circle cx="5" cy="11" r="3" />
					<path d="M7.2 8.8 13.5 2.5" />
					<path d="M11 5l2 2" />
					<path d="M9 7l1.5 1.5" />
				</svg>
			);
		case "creditCard":
			// クレジットカード。枠 + 磁気ストライプ + 署名欄の短線。
			return (
				<svg {...common}>
					<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
					<path d="M1.5 6.5h13" />
					<path d="M4 10.25h3.5" />
				</svg>
			);
		case "pin":
			// プッシュピン。ヘッド(円) + 針(先端へ細くなる形) + 上部のツマミ。
			return (
				<svg {...common}>
					<path d="M6 2.5h4l-.4 4.4 2.4 2.1v1.5H4v-1.5l2.4-2.1z" />
					<path d="M8 10.5V14" />
				</svg>
			);
		case "alertTriangle":
			return (
				<svg {...common}>
					<path d="M7.13 2.5 1.9 12a1 1 0 0 0 .88 1.5h10.44a1 1 0 0 0 .88-1.5L8.87 2.5a1 1 0 0 0-1.74 0z" />
					<path d="M8 5.5v3.5" />
					<path d="M8 11.2h.01" />
				</svg>
			);
		case "xCircle":
			return (
				<svg {...common}>
					<circle cx="8" cy="8" r="6" />
					<path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" />
				</svg>
			);
		default:
			return null;
	}
}
